import { instanceBo } from "../utils/axios.create";
import {
	CreatePayer,
	DeletePayer,
	PatternImpact,
	PayerDependencies,
	PayerResponse,
	UpdatePayer,
} from "@/domain/payer.interface";

const findAll = async (): Promise<PayerResponse[]> => {
	return instanceBo
		.get("/payers")
		.then((res) => res.data.body)
		.catch((err) => {
			throw new Error(err.response.data);
		});
};

/**
 * SIMS-8647 AC2/AC3/AC4 — the counts the delete confirmation needs.
 *
 * Errors are RETHROWN rather than defaulted to zero: the dialog blocks a delete it cannot verify,
 * because "no dependencies" on a failed request is exactly how a payer with hundreds of registered
 * members would be offered for deletion.
 */
const getDependencies = async (id: string): Promise<PayerDependencies> => {
	return instanceBo
		.get(`/payers/${id}/dependencies`)
		.then((res) => res.data.body);
};

/**
 * SIMS-8622 AC1 — the counts behind the pattern-change warning.
 *
 * Errors are RETHROWN rather than defaulted: the warning exists to inform a destructive-ish
 * decision, and a silent 0 would tell the administrator "nothing breaks" when we simply do not
 * know. The editor treats a failure as "cannot check" and says so.
 */
const getPatternImpact = async (
	id: string,
	pattern: string,
): Promise<PatternImpact> => {
	return instanceBo
		.get(`/payers/${id}/pattern-impact`, { params: { pattern } })
		.then((res) => res.data.body);
};

const createPayer = async (data: CreatePayer): Promise<PayerResponse> => {
	return instanceBo
		.post("/payers", data)
		.then((res) => res.data.body)
		.catch((err) => {
			return Promise.reject(err.response?.data || err);
		});
};

const updatePayer = async (data: UpdatePayer): Promise<PayerResponse> => {
	const { _id, ...updateData } = data;
	return instanceBo
		.put(`/payers/${_id}`, updateData)
		.then((res) => res.data.body)
		.catch((err) => {
			return Promise.reject(err.response?.data || err);
		});
};

const deletePayer = async (data: DeletePayer): Promise<void> => {
	const { _id, ...deleteData } = data;
	return instanceBo
		.delete(`/payers/${_id}`, { data: deleteData })
		.then(() => undefined)
		.catch((err) => {
			return Promise.reject(err.response?.data || err);
		});
};

export const payerService = {
	findAll,
	getDependencies,
	getPatternImpact,
	createPayer,
	updatePayer,
	deletePayer,
};
